import { api, ApiError } from './api.js';

/** Garagem e envio do CRLV-e (rotas /v1/vehicles). */
export const vehicles = {
  /** @returns {Promise<{ items: { vehicle: any, access: any | null, claim: any | null }[] }>} */
  list: () => api('GET', '/v1/vehicles'),
  get: (id) => api('GET', `/v1/vehicles/${encodeURIComponent(id)}`),
  setNickname: (id, nickname) => api('PATCH', `/v1/vehicles/${encodeURIComponent(id)}`, { nickname }),
  registrationConfig: () => api('GET', '/v1/vehicles/registration-config'),
  submitClaim: (id, fileName) => api('POST', `/v1/vehicles/claims/${encodeURIComponent(id)}/submit`, { fileName }),
  createClaim: (data) => api('POST', '/v1/vehicles/claims', data),
  cancelClaim: (id) => api('DELETE', `/v1/vehicles/claims/${encodeURIComponent(id)}`),
  // recusado: sai da garagem, mas não é apagado (cadastrar de novo reaproveita o veículo)
  dismissClaim: (id) => api('POST', `/v1/vehicles/claims/${encodeURIComponent(id)}/dismiss`),
  fileUrl: (id) => api('GET', `/v1/vehicles/claims/${encodeURIComponent(id)}/file`),
  // pessoa autorizada (o dono compartilha o veículo com 1 pessoa)
  share: (id) => api('GET', `/v1/vehicles/${encodeURIComponent(id)}/share`),
  invite: (id, email) => api('POST', `/v1/vehicles/${encodeURIComponent(id)}/share`, { email }),
  cancelInvite: (id) => api('DELETE', `/v1/vehicles/${encodeURIComponent(id)}/share/invite`),
  removeAuthorized: (id) => api('DELETE', `/v1/vehicles/${encodeURIComponent(id)}/share/authorized`),
  leave: (id) => api('DELETE', `/v1/vehicles/${encodeURIComponent(id)}/share/me`),
  invites: () => api('GET', '/v1/vehicle-invites'),
  acceptInvite: (id) => api('POST', `/v1/vehicle-invites/${encodeURIComponent(id)}/accept`),
  declineInvite: (id) => api('POST', `/v1/vehicle-invites/${encodeURIComponent(id)}/decline`),
  // venda pelo app: o vendedor gera link/QR; o comprador aceita e vira dono provisório
  transfer: (id) => api('GET', `/v1/vehicles/${encodeURIComponent(id)}/transfer`),
  sell: (id, km) => api('POST', `/v1/vehicles/${encodeURIComponent(id)}/transfer`, { km }),
  /** Andamento de uma venda minha (vale mesmo depois que o comprador aceitou e eu perdi o acesso ao carro). */
  transferStatus: (id, transferId) => api('GET', `/v1/vehicles/${encodeURIComponent(id)}/transfer/${encodeURIComponent(transferId)}`),
  cancelSale: (id) => api('DELETE', `/v1/vehicles/${encodeURIComponent(id)}/transfer`),
  soldOffline: (id, km) => api('POST', `/v1/vehicles/${encodeURIComponent(id)}/transfer/offline`, km ? { km } : {}),
  transferPreview: (token) => api('GET', `/v1/vehicle-transfers/${encodeURIComponent(token)}`),
  acceptTransfer: (token) => api('POST', `/v1/vehicle-transfers/${encodeURIComponent(token)}/accept`),
  declineTransfer: (token) => api('POST', `/v1/vehicle-transfers/${encodeURIComponent(token)}/decline`),
  // outra pessoa enviou o CRLV-e do meu carro: "Sim, vendi" ou "Não reconheço"
  confirmSale: (id, docId) => api('POST', `/v1/vehicles/${encodeURIComponent(id)}/ownership-claims/${encodeURIComponent(docId)}/confirm`),
  contestClaim: (id, docId, note) => api('POST', `/v1/vehicles/${encodeURIComponent(id)}/ownership-claims/${encodeURIComponent(docId)}/contest`, note ? { note } : {}),
};

/** Uploads the CRLV-e for Vio and returns server-bound vehicle fields and scanId. */
export async function scanVehicleDocument(file) {
  const prepared = await prepareFile(file);
  const { scanId, upload } = await api('POST', '/v1/vehicles/document-scans', { contentType: prepared.type, size: prepared.blob.size });
  const form = new FormData();
  for (const [key, value] of Object.entries(upload.fields)) form.append(key, value);
  form.append('file', prepared.blob, prepared.name);
  await postWithProgress(upload.url, form, () => {});
  return api('POST', `/v1/vehicles/document-scans/${encodeURIComponent(scanId)}/recognize`);
}

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Frente, verso e alguma foto extra (ex.: documento dobrado). */
export const MAX_PHOTOS = 4;
const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png'];

/**
 * Prepara o arquivo: PDF vai como está; foto é reduzida para JPEG (até 2400 px, ~1 MB),
 * o que também converte HEIC/WebP do celular num formato que o admin consegue abrir.
 * @param {File} file
 * @returns {Promise<{ blob: Blob, type: string, name: string }>}
 */
export async function prepareFile(file) {
  if (file.type === 'application/pdf') {
    if (file.size > MAX_FILE_BYTES) throw new ApiError(0, 'FILE_TOO_LARGE', 'O PDF passa de 10 MB.');
    return { blob: file, type: file.type, name: file.name };
  }
  if (!file.type.startsWith('image/')) throw new ApiError(0, 'INVALID_FILE', 'Envie o PDF do CRLV-e ou uma foto do documento.');
  if (ALLOWED.includes(file.type) && file.size <= 1.5 * 1024 * 1024) return { blob: file, type: file.type, name: file.name };
  try {
    const blob = await downscale(file, 2400, 0.85);
    return { blob, type: 'image/jpeg', name: file.name.replace(/\.[^.]+$/, '') + '.jpg' };
  } catch {
    if (ALLOWED.includes(file.type) && file.size <= MAX_FILE_BYTES) return { blob: file, type: file.type, name: file.name };
    throw new ApiError(0, 'INVALID_FILE', 'Não foi possível ler esta foto. Tente tirar outra ou enviar o PDF.');
  }
}

export async function downscale(file, maxSide, quality) {
  const img = await loadImage(file);
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  img.close?.();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('toBlob');
  return blob;
}

/** Junta as fotos na vertical (largura até 1600 px, faixa branca entre elas) em um JPEG. */
async function combinePhotos(files) {
  if (files.length > MAX_PHOTOS) throw new ApiError(0, 'TOO_MANY_PHOTOS', `Envie no máximo ${MAX_PHOTOS} fotos.`);
  let imgs;
  try {
    imgs = await Promise.all(files.map(loadImage));
  } catch {
    throw new ApiError(0, 'INVALID_FILE', 'Não foi possível ler uma das fotos. Tire de novo.');
  }
  const width = Math.min(1600, Math.max(...imgs.map((i) => i.width)));
  const GAP = 24;
  let heights = imgs.map((i) => Math.round((i.height * width) / i.width));
  let total = heights.reduce((a, h) => a + h, 0) + GAP * (imgs.length - 1);
  const shrink = Math.min(1, 7000 / total); // limite de memória do canvas no celular
  const w = Math.round(width * shrink);
  heights = heights.map((h) => Math.round(h * shrink));
  total = heights.reduce((a, h) => a + h, 0) + GAP * (imgs.length - 1);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = total;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, total);
  let y = 0;
  imgs.forEach((img, i) => {
    ctx.drawImage(img, 0, y, w, heights[i]);
    y += heights[i] + GAP;
    img.close?.();
  });
  for (const quality of [0.85, 0.7, 0.55]) {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= MAX_FILE_BYTES) return { blob, type: 'image/jpeg', name: `crlv-${imgs.length}-fotos.jpg` };
  }
  throw new ApiError(0, 'FILE_TOO_LARGE', 'As fotos ficaram grandes demais. Tente tirar de novo com menos zoom.');
}

async function loadImage(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* tenta com <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Envia o documento de um envio (claim): pede o POST assinado, manda direto para o S3 e conclui.
 * O arquivo não passa pela nossa API.
 * @param {string} claimId
 * @param {File} file
 * @param {(fraction: number) => void} [onProgress]
 */
export function uploadClaimFile(claimId, file, onProgress = () => {}) {
  return uploadClaimFiles(claimId, [file], onProgress);
}

/**
 * Várias fotos (frente e verso): viram UMA imagem, uma embaixo da outra, para o envio
 * continuar sendo um arquivo só (o admin vê tudo junto). Um PDF ou uma foto vão como estão.
 * @param {string} claimId
 * @param {File[]} files
 * @param {(fraction: number) => void} [onProgress]
 */
export async function uploadClaimFiles(claimId, files, onProgress = () => {}) {
  const prepared = files.length > 1 ? await combinePhotos(files) : await prepareFile(files[0]);
  const { upload } = await api('POST', `/v1/vehicles/claims/${encodeURIComponent(claimId)}/upload`, { contentType: prepared.type, size: prepared.blob.size });
  const form = new FormData();
  for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
  form.append('file', prepared.blob, prepared.name); // "file" precisa ser o último campo
  await postWithProgress(upload.url, form, onProgress);
  return api('POST', `/v1/vehicles/claims/${encodeURIComponent(claimId)}/submit`, { fileName: prepared.name.slice(0, 120) });
}

export function postWithProgress(url, form, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, 'UPLOAD_FAILED', 'O envio do arquivo falhou. Tente de novo.')));
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Sem conexão para enviar o arquivo. Tente de novo.'));
    xhr.send(form);
  });
}

export const CLAIM_STATUS = {
  PENDING_UPLOAD: { label: 'Falta o documento', color: 'warning' },
  PENDING_REVIEW: { label: 'Em análise', color: 'warning' },
  REJECTED: { label: 'Documento recusado', color: 'danger' },
  APPROVED: { label: 'Aprovado', color: 'success' },
};
export const RELATION = {
  OWNER: { label: 'Dono', color: 'success' },
  COMPANY_OWNER: { label: 'Empresa', color: 'success' },
  AUTHORIZED_USER: { label: 'Autorizado', color: 'primary' },
  PROVISIONAL_OWNER: { label: 'Dono provisório', color: 'warning' },
};
/** Dono comprovado (pode vender e compartilhar); o provisório ainda precisa do CRLV-e no nome dele. */
export const VERIFIED_OWNER = ['OWNER', 'COMPANY_OWNER'];
export const REJECTION_TEXT = {
  ILLEGIBLE: 'O documento está ilegível ou incompleto. Envie o PDF do CRLV-e ou uma foto nítida do documento inteiro.',
  DATA_MISMATCH: 'Os dados do documento não conferem com a placa ou o RENAVAM informados.',
  NOT_OWNER: 'O documento não está no seu nome. Peça ao dono para compartilhar o veículo com você: no app dele, em Veículo › Pessoa autorizada.',
  OTHER: 'O documento não pôde ser aprovado.',
};

/** "Fiat Uno 2015" */
export const vehicleName = (v) => [v?.brand, v?.model, v?.modelYear].filter(Boolean).join(' ');

/**
 * Linhas de veículo. LIGHT é o padrão (veículos antigos vêm sem category).
 * AGRO usa horímetro (meterUnit HOURS) e é identificado pelo número de série; placa e RENAVAM são opcionais.
 */
export const CATEGORY = {
  LIGHT: { key: 'LIGHT', line: 'Leve', label: 'Linha leve', short: 'Leves', name: 'Carro, moto ou utilitário', kind: 'Carro', examples: 'Passeio, SUV, picape, van e moto', icon: 'tc-car', tags: ['Placa + RENAVAM', 'Km'] },
  HEAVY: { key: 'HEAVY', line: 'Pesada', label: 'Linha pesada', short: 'Pesados', name: 'Caminhão ou ônibus', kind: 'Caminhão', examples: 'Cavalo mecânico, toco, truck, ônibus e micro-ônibus', icon: 'tc-truck', tags: ['Placa + RENAVAM', 'Km', 'Revisões diesel'] },
  AGRO: { key: 'AGRO', line: 'Agrícola', label: 'Agrícola', short: 'Agrícolas', name: 'Máquina agrícola', kind: 'Máquina', examples: 'Trator, colheitadeira, pulverizador e plantadeira', icon: 'tc-tractor', tags: ['Nº de série', 'Horímetro', 'Nota fiscal'] },
};
export const CATEGORY_KEYS = ['LIGHT', 'HEAVY', 'AGRO'];
export const categoryOf = (v) => CATEGORY[v?.category] ?? CATEGORY.LIGHT;
export const meterOf = (v) => (v?.meterUnit === 'HOURS' || v?.category === 'AGRO' ? 'HOURS' : 'KM');

/** Como o veículo é reconhecido quando não tem apelido nem modelo: placa, ou o número de série (máquina). */
export const vehicleId = (v) => v?.plateFormatted || v?.plate || (v?.serialNumber ? `Série ${v.serialNumber}` : '');
